import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import userServices from "services/userServices";
import PackageDetails from "./PackageDetails.js";
import BaggageDetails from "./BaggageDetails.js";
import ReviewCard from "./ReviewCard.js";
import EditHistoryCard from "./EditHistoryCard.js";
import Header from "./Header";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Container,
  Spinner,
} from "reactstrap";
import UserCard from "../../components/UserCard";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";
import { useNotification } from "providers/NotificationProvider";
import helpers from "utils/helpers";
import { useAuth } from "context/AuthContext";

function ListingDetails() {
  const { type, listingId } = useParams();
  const { permission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [changedPaths, setChangedPaths] = useState([]);

  const fetchListingDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userServices.getListingDetails(listingId, type);
      const dataObj = res?.data?.data || res?.data || res;
      setListing(dataObj);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [listingId, type]);

  useEffect(() => {
    fetchListingDetails();
  }, [fetchListingDetails]);

  // Draft guard — redirect if listing is a draft
  useEffect(() => {
    if (listing && listing?.status === "draft") {
      navigate("/admin/listings", { replace: true });
    }
  }, [listing, navigate]);

  const handleToggleAccount = async (row, status, type, adminRemarks = {}) => {
    setIsStatusUpdating(true);
    try {
      setLoadingAction(status);
      const formattedType = type?.charAt(0).toUpperCase() + type?.slice(1);

      const res = await userServices.updateListings(row._id, {
        status,
        type: formattedType,
        userData: row.userData,
        ...adminRemarks,
      });
      const payload = res?.data || res;
      if (payload?.success) {
        notify("success", t(payload?.messageKey || "page.userListings.toast.listingUpdated", payload?.message || t("page.userListings.toast.listingUpdated", "Listing updated successfully")));
        const listingObj = payload?.data || payload;
        setListing(listingObj);
      } else {
        const errorMessage = payload?.messageKey || payload?.error || "page.userListings.toast.listingStatusUpdateFailed";
        notify("error", t(errorMessage, payload?.message || t("page.userListings.toast.listingStatusUpdateFailed", "Failed to update listing status")));
      }
    } catch (err) {
      const errPayload = err.response?.data || {};
      const errorMessage = errPayload.messageKey || errPayload.error || "page.userListings.toast.listingStatusUpdateFailed";
      notify("error", t(errorMessage, errPayload.message || t("page.userListings.toast.listingStatusUpdateFailed", "Failed to update listing status")));
    } finally {
      setIsStatusUpdating(false);
      setLoadingAction(null);
    }
  };

  const handleLatestChanges = useCallback((paths) => {
    setChangedPaths(paths);
  }, []);

  if (loading) {
    return (
      <div className="center-loader">
        <div className="center-loader-content">
          <Spinner/>
          <p className="mt-3 text-muted">
            {t("common.loading", "Loading....")}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <Container fluid className="user-details-wrapper mt-4">
      <Row>
        <Col lg="8">
          <Card className="shadow border-0 mb-3">
            <CardHeader className="bg-transparent">
              <Header
                journeyDetails={listing}
                type={type}
                handleToggleAccount={handleToggleAccount}
                goBack={() => navigate(-1)}
                isLoading={isStatusUpdating}
                loadingAction={loadingAction}
                changedPaths={changedPaths}
              />
            </CardHeader>
          </Card>
          <Card className="shadow border-0 mb-3">
            <CardBody>
              {type === helpers.LISTING_TABLE_FILTERS.SENDER && (
                <PackageDetails
                  packageData={listing.packageDetails}
                  pickupData={listing.pickUpDetails}
                  changedPaths={changedPaths}
                />
              )}

              {type === helpers.LISTING_TABLE_FILTERS.TRAVELER && (
                <BaggageDetails
                  baggageData={listing.baggageDetails}
                  documentData={listing.documentDetails}
                  pickupData={listing.pickUpDetails}
                  changedPaths={changedPaths}
                  listing={listing}
                />
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          {listing?.userData && (
            <UserCard
              user={listing.userData}
              title={
                type === helpers.LISTING_TABLE_FILTERS.SENDER
                  ? t("page.orders.senderProfile", "Sender Profile")
                  : t("page.orders.travelerProfile", "Traveler Profile")
              }
            />
          )}
          {permission.permissions.modules.listings.update &&
            <ReviewCard
              listing={listing}
              type={type}
              handleToggleAccount={handleToggleAccount}
              isStatusUpdating={isStatusUpdating}
              loadingAction={loadingAction}
            />
          }
          <EditHistoryCard
            listingId={listingId}
            currentListing={listing}
            listingType={type}
            onLatestChanges={handleLatestChanges}
          />
        </Col>
      </Row>
    </Container>
  );
}

export default ListingDetails;
